import { Avatar, Box, Flex, Text } from '@chakra-ui/react';
import { useGetMembersQuery } from 'gql/graphql';
import {
  Select as ReactSelect,
  chakraComponents,
  OptionProps,
  GroupBase,
  FilterOptionOption,
} from 'chakra-react-select';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MemberOption, AddMembersDropdownProps } from './types';
import {
  filterOptions,
  getLoadingMessage,
  getNoOptionsMessage,
  matchPastedEmails,
} from './utils';

const MEMBERS_PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 400;

export const customComponents = {
  Option: (
    props: OptionProps<MemberOption, boolean, GroupBase<MemberOption>>,
  ) => {
    const { label, email, icon } = props.data;
    return (
      <chakraComponents.Option {...props}>
        <Flex gap={2} align='center'>
          <Avatar
            size='sm'
            name={label}
            src={icon ?? ''}
            bg='teal.700'
            textColor='white'
            // if we want to use initials, remove src
          />
          <Flex direction='column' gap={0.5}>
            <Text>{label}</Text>
            <Text variant='subtle'>{email}</Text>
          </Flex>
        </Flex>
      </chakraComponents.Option>
    );
  },
  //For each selected multivalue item, we can manipulate its style
  MultiValue: (props: any) => {
    const highlight = props.data.highlight;
    return (
      <Box
        background={highlight ? 'red.100' : undefined}
        sx={highlight ? { '*': { color: 'red' } } : {}}
        borderRadius='6px'
        display='flex'
        alignItems='center'
      >
        <chakraComponents.MultiValue {...props} />
      </Box>
    );
  },
};

export const AddMembersDropdown: React.FC<AddMembersDropdownProps> = ({
  onMembersSelect,
  value,
}) => {
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchDebounceRef = useRef<number | null>(null);

  const clearPendingSearchDebounce = useCallback(() => {
    if (searchDebounceRef.current !== null) {
      window.clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => clearPendingSearchDebounce();
  }, [clearPendingSearchDebounce]);

  const trimmedDebounced = debouncedSearch.trim();
  const hasServerSearch = trimmedDebounced.length > 0;

  const {
    data: baselineMembersData,
    loading: baselineMembersLoading,
  } = useGetMembersQuery({
    variables: {
      skip: 0,
      take: MEMBERS_PAGE_SIZE,
    },
    fetchPolicy: 'cache-first',
  });

  const {
    data: searchMembersData,
    loading: searchMembersLoading,
    error: searchMembersError,
  } = useGetMembersQuery({
    variables: {
      skip: 0,
      take: MEMBERS_PAGE_SIZE,
      query: trimmedDebounced,
    },
    skip: !hasServerSearch,
    fetchPolicy: 'cache-and-network',
  });

  const membersData = hasServerSearch
    ? searchMembersData
    : baselineMembersData;

  const members: MemberOption[] = useMemo(
    () =>
      (membersData?.organization?.members?.items || []).map((member) => ({
        value: member.id,
        label: `${member.givenName} ${member.familyName}`,
        email: member.email,
        icon: member.picture,
      })),
    [membersData?.organization?.members?.items],
  );

  const membersWithSelections = useMemo(() => {
    const byId = new Map(members.map((m) => [m.value, m]));
    for (const v of value || []) {
      if (!byId.has(v.value)) {
        byId.set(v.value, {
          value: v.value,
          label: v.label,
          email: v.email,
          icon: v.icon,
        });
      }
    }
    return Array.from(byId.values());
  }, [members, value]);

  const filterOption = useCallback(
    (candidate: FilterOptionOption<MemberOption>, input: string) => {
      if (searchText.trim() !== debouncedSearch.trim()) {
        return true;
      }
      return filterOptions(candidate, input);
    },
    [searchText, debouncedSearch],
  );

  const handleInputChange = useCallback(
    (newValue: string) => {
      setSearchText(newValue);
      clearPendingSearchDebounce();
      if (newValue.trim() === '') {
        setDebouncedSearch('');
        return;
      }
      searchDebounceRef.current = window.setTimeout(() => {
        searchDebounceRef.current = null;
        setDebouncedSearch(newValue);
      }, SEARCH_DEBOUNCE_MS);
    },
    [clearPendingSearchDebounce],
  );

  const handleChange = (
    selectedOptions: readonly MemberOption[] | null,
  ): void => {
    const nextLen = selectedOptions?.length ?? 0;
    const prevLen = value?.length ?? 0;

    if (selectedOptions) {
      onMembersSelect([...selectedOptions]);
    } else {
      onMembersSelect([]);
    }

    if (nextLen > prevLen) {
      clearPendingSearchDebounce();
      setDebouncedSearch('');
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const pastedText = e.clipboardData.getData('text');
    const merged = matchPastedEmails(
      pastedText,
      membersWithSelections,
      value || [],
    );

    if (merged && merged.length > 0) {
      e.preventDefault();
      onMembersSelect(merged);
    }
  };

  const valueMap = new Map(
    (value || []).map((v: MemberOption) => [v.value, v.highlight]),
  );
  const mergedMembers = membersWithSelections.map((option) => ({
    ...option,
    highlight: Boolean(valueMap.get(option.value)),
  }));

  const isInitialLoading =
    !hasServerSearch &&
    baselineMembersLoading &&
    baselineMembersData === undefined;

  const awaitingSearchResponse =
    hasServerSearch &&
    !searchMembersError &&
    (searchMembersLoading || searchMembersData === undefined);

  const showSelectLoading = isInitialLoading || awaitingSearchResponse;

  return (
    <Box onPaste={handlePaste}>
      <ReactSelect
        aria-label='Add Users Select'
        options={mergedMembers}
        value={value}
        onChange={handleChange}
        onInputChange={handleInputChange}
        blurInputOnSelect={false}
        placeholder='Add Users'
        isClearable
        isDisabled={isInitialLoading}
        isLoading={showSelectLoading}
        noOptionsMessage={getNoOptionsMessage}
        loadingMessage={getLoadingMessage}
        components={customComponents}
        filterOption={filterOption}
        isMulti
      />
    </Box>
  );
};
